import { del, get, HttpResponse, patch, post, postBlob } from '@/api/Api';
import { TokenType } from '@/enums/TokenType';
import { THGCar, THGCarCreateResponse } from '@/interfaces/thg/THGCar';
import { THGUpdateCarData } from '@/interfaces/thg/THGUpdateCarData';
import { THGCarPaginatedResponse } from '@/interfaces/thg/THGCarPaginatedResponse';
import { THGCarFilterParam } from '@/interfaces/thg/THGCarFilterParam';
import { formatDate } from '@/utils/misc';
import { api_url } from '@/api/thg/api';
import { parseISO } from 'date-fns';
import { SortOrder } from '@/enums/misc';
import { THGCarCountUsersResponseSchema } from '@/interfaces/thg/THGCarCountUsersResponseSchema';

/**
 * Delete a car.
 *
 * @param carId
 * @return http response to await
 */
export async function deleteTHGCar(carId: string): Promise<HttpResponse<null>> {
    return await del<null>(`${api_url()}/crm/cars/${carId}`, null, TokenType.THG_API_KEY);
}

/**
 * Get a thg car by id from thg api.
 *
 * @param carId car ID
 * @return http response to await
 */
export async function getTHGCar(carId: string): Promise<HttpResponse<THGCar>> {
    const res = await get<THGCar>(`${api_url()}/crm/cars/${carId}`, TokenType.THG_API_KEY);

    if (res.parsedBody?.uba_submitted_date) {
        res.parsedBody.uba_submitted_date = parseISO(res.parsedBody.uba_submitted_date as string);
    }

    if (res.parsedBody?.deregistration_date) {
        res.parsedBody.deregistration_date = parseISO(res.parsedBody.deregistration_date as string);
    }

    return res;
}

export function buildTHGCarFilterPayload(filterParams: THGCarFilterParam) {
    const res: Record<string, any> = {};

    if (filterParams.year) {
        res.year = filterParams.year;
    }

    if (filterParams.start_create_date && filterParams.end_create_date) {
        res.start_create_date = formatDate(filterParams.start_create_date, 'yyyy-MM-dd');
        res.end_create_date = formatDate(filterParams.end_create_date, 'yyyy-MM-dd');
    }

    if (filterParams.status) {
        res.status = filterParams.status;
    }

    if (filterParams.vin) {
        res.vin = filterParams.vin;
    }

    if (filterParams.drive_code) {
        res.drive_code = filterParams.drive_code;
    }

    if (filterParams.license_plate) {
        res.license_plate = filterParams.license_plate;
    }

    if (filterParams.vehicle_class) {
        res.vehicle_class = filterParams.vehicle_class;
    }

    if (filterParams.uba_submitted_date) {
        res.uba_submitted_date = formatDate(filterParams.uba_submitted_date, 'yyyy-MM-dd');
    }

    if (filterParams.payoff_model) {
        res.payoff_model = filterParams.payoff_model;
    }

    if (filterParams.min_co2_equiv !== undefined && filterParams.min_co2_equiv !== null) {
        res.min_co2_equiv = filterParams.min_co2_equiv;
    }

    if (filterParams.max_co2_equiv !== undefined && filterParams.max_co2_equiv !== null) {
        res.max_co2_equiv = filterParams.max_co2_equiv;
    }

    if (filterParams.vins.length) {
        if (Array.isArray(filterParams.vins)) {
            res.vins = filterParams.vins.join(',');
        } else {
            res.vins = filterParams.vins;
        }
    }

    if (filterParams.tags.length) {
        if (Array.isArray(filterParams.tags)) {
            res.tags = filterParams.tags.map((tag) => tag._id).join(',');
        } else {
            res.tags = filterParams.tags;
        }
    }

    if (filterParams.not_tags.length) {
        if (Array.isArray(filterParams.not_tags)) {
            res.not_tags = filterParams.not_tags.map((tag) => tag._id).join(',');
        } else {
            res.not_tags = filterParams.not_tags;
        }
    }

    if (filterParams.is_card !== null) {
        res.is_card = filterParams.is_card ? '1' : '0';
    }

    if (filterParams.creation_source) {
        res.creation_source = filterParams.creation_source;
    }

    if (filterParams.user_email) {
        res.user_email = filterParams.user_email;
    }

    if (filterParams.user_id) {
        res.user_id = filterParams.user_id;
    }

    return res;
}

export function buildTHGCarParams(
    sortField?: string,
    sortOrder?: SortOrder,
    pageSize?: number,
    pageNum?: number,
): URLSearchParams {
    const params = new URLSearchParams();

    if (pageSize) {
        params.set('page_size', pageSize.toString());
    }

    if (pageNum) {
        params.set('page_num', pageNum.toString());
    }

    if (sortField) {
        params.set('sort_field', sortField);

        if (sortOrder) {
            params.set('sort_order', sortOrder.toString());
        }
    }

    return params;
}

/**
 * Export the current car list.
 *
 * @param filterParams filter parameters
 * @param sortField sort field
 * @param sortOrder sort oder
 */
export async function exportTHGCars(
    filterParams: THGCarFilterParam,
    sortField?: string,
    sortOrder?: SortOrder,
) {
    const params = buildTHGCarParams(sortField, sortOrder);
    const payload = buildTHGCarFilterPayload(filterParams);

    return await postBlob(`${api_url()}/crm/cars/export?${params}`, payload, TokenType.THG_API_KEY);
}

/**
 * Export the current car list to the uba Excel file.
 *
 * @param filterParams
 */
export async function exportTHGCarsUBA(filterParams: THGCarFilterParam) {
    const payload = buildTHGCarFilterPayload(filterParams);

    return await postBlob(`${api_url()}/crm/cars/export-uba`, payload, TokenType.THG_API_KEY);
}

/**
 * Get the number of users affected by car query.
 *
 * @param filterParams
 */
export async function countAffectedTHGUsers(
    filterParams: THGCarFilterParam,
): Promise<HttpResponse<THGCarCountUsersResponseSchema>> {
    const payload = buildTHGCarFilterPayload(filterParams);

    return await post<THGCarCountUsersResponseSchema>(
        `${api_url()}/crm/cars/count/affected-users`,
        payload,
        TokenType.THG_API_KEY,
    );
}

/**
 * Fetch all thg vehicles in a paginated way.
 *
 * @param pageNum page
 * @param pageSize page size
 * @param filterParams filter parameters
 * @param sortField sort field
 * @param sortOrder sort oder
 */
export async function fetchTHGCars(
    filterParams?: THGCarFilterParam,
    sortField?: string,
    sortOrder?: SortOrder,
    pageSize?: number,
    pageNum?: number,
): Promise<HttpResponse<THGCarPaginatedResponse>> {
    const params = buildTHGCarParams(sortField, sortOrder, pageSize, pageNum);
    let payload = undefined;

    if (filterParams) {
        payload = buildTHGCarFilterPayload(filterParams);
    }

    return await post<THGCarPaginatedResponse>(
        `${api_url()}/crm/cars?${params}`,
        payload,
        TokenType.THG_API_KEY,
    );
}

/**
 * Add a THG car.
 *
 * @param payload
 * @param skip_mail_sending
 */
export async function addTHGCar(
    payload: THGCar,
    skip_mail_sending = true,
): Promise<HttpResponse<THGCarCreateResponse>> {
    return await post<THGCarCreateResponse>(
        `${api_url()}/cars?force_payoff_override=${true}&skip_mail_sending=${skip_mail_sending}`,
        payload,
        TokenType.THG_API_KEY,
    );
}

/**
 * Save a THG Car to database.
 *
 * @param carId car uuid as string
 * @param car update data
 */
export async function saveTHGCar(
    carId: string,
    car: THGUpdateCarData,
): Promise<HttpResponse<null>> {
    return await patch<null>(`${api_url()}/cars/${carId}`, car, TokenType.THG_API_KEY);
}
